import { useQuery } from '@tanstack/react-query'
import { action, makeObservable, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { useEffect, useMemo } from 'react'
import type { Equal, Expect } from 'type-testing'
import type { ThisLocationVariable } from '@companion-app/shared/ControlLocation.js'
import { EntityModelType, type SomeEntityModel } from '@companion-app/shared/Model/EntityModel.js'
import type { VariableValue, VariableValues } from '@companion-app/shared/Model/Variables.js'
import type { DropdownChoiceInt } from '~/Components/DropdownChoices.js'
import { trpc } from '~/Resources/TRPC.js'

export class LocalVariablesStore {
	readonly controlId: string

	#variables = observable.map<string, SomeEntityModel>()
	#values = observable.map<string, VariableValue | undefined>()

	constructor(controlId: string) {
		this.controlId = controlId

		makeObservable(this, {
			setEntities: action,
		})
	}

	setEntities(localVariables: SomeEntityModel[]): void {
		this.#variables.replace(localVariables.map((v): [string, SomeEntityModel] => [v.id, v]))
	}

	setValues(values: VariableValues): void {
		this.#values.replace(Object.entries(values))
	}

	getValue = (variableName: string): VariableValue | undefined => {
		return this.#values.get(variableName)
	}

	getOptions = computedFn(
		(entityType: EntityModelType | null, internalParser: boolean, isLocatedInGrid: boolean): DropdownChoiceInt[] => {
			let fixedVariables: DropdownChoiceInt[] = []

			if (isLocatedInGrid) {
				fixedVariables = ControlLocalVariables
				if (internalParser && entityType === EntityModelType.Action) {
					fixedVariables = ControlWithInternalLocalVariables
				}
			}

			const dynamicVariables: DropdownChoiceInt[] = []
			for (const entity of this.#variables.values()) {
				if (entity.type !== EntityModelType.Feedback) continue
				if (entity.disabled || !entity.variableName) continue

				dynamicVariables.push({
					value: `local:${entity.variableName}`,
					label: entity.headline || entity.variableName,
				})
			}

			return [...fixedVariables, ...dynamicVariables]
		}
	)
}

export function useLocalVariablesStore(
	controlId: string,
	localVariables: SomeEntityModel[] | null
): LocalVariablesStore {
	const store = useMemo(() => new LocalVariablesStore(controlId), [controlId])

	useEffect(() => {
		if (localVariables) store.setEntities(localVariables)
	}, [store, localVariables])

	const query = useQuery(
		trpc.controls.entities.localVariableValues.queryOptions(
			{
				controlId,
			},
			{
				refetchInterval: 1000,
				refetchOnWindowFocus: true,
				refetchOnMount: true,
			}
		)
	)

	useEffect(() => {
		store.setValues(query.data || {})
	}, [query.data, store])

	return store
}

export const ControlLocalVariables = [
	{
		value: 'this:page',
		label: 'This page',
	},
	{
		value: 'this:column',
		label: 'This column',
	},
	{
		value: 'this:row',
		label: 'This row',
	},
	{
		value: 'this:location',
		label: 'This location (eg 1/2/3)',
	},
	{
		value: 'this:active',
		label: 'Whether the button is in the pushed state',
	},
	{
		value: 'this:step',
		label: 'The current step of this button',
	},
	{
		value: 'this:step_count',
		label: 'The number of step of this button',
	},
	{
		value: 'this:button_status',
		label: 'The status of this button',
	},
	{
		value: 'this:actions_running',
		label: 'Whether actions are running from this button',
	},
	{
		value: 'this:page_name',
		label: 'This page name',
	},
] as const satisfies DropdownChoiceInt[]

// @ts-expect-error Type used only to assert a type condition
type _VerifyControlVariablesDropdownIsComplete = Expect<
	Equal<(typeof ControlLocalVariables)[number]['value'], ThisLocationVariable>
>

export const ControlWithInternalLocalVariables: DropdownChoiceInt[] = [
	...ControlLocalVariables,
	{
		value: 'this:surface_id',
		label: 'The id of the surface triggering this action',
	},
]

/** Variable picker entry injected for fields that use deferred parsing (e.g. set-value actions). */
export const DeferredParsingContextVariables: DropdownChoiceInt[] = [
	{ value: 'this:current', label: 'Current value of this variable' },
]
